/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useCustomToast } from '@/hooks/use-custom-toast';
import React, { memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  createLabelCustomerValidationSchema,
  TCreateLabelCustomerValidation,
  useCreateLabelCustomerMutation,
} from '../../../api/create-label-customer.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CUSTOMER_QUERY_KEY } from '../../../api/get-list-label-customer.api';
import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import { handleDomBubbleEvent } from '@/utils/dom';
// import { GENERAL_LABEL_COLOR } from '@/features/settings/label/_shared/constants/general-label-color';
import { GENERAL_LABEL_COLOR } from '@/features/cases/_shared/constants/cases.contants';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

export type TCreateLabelCustomerModalType = 'CreateLabelCustomerModal';
export interface ICreateLabelCustomerModalProps {}
const CreateLabelCustomerModal = memo((_props: ICreateLabelCustomerModalProps) => {
  const customerModuleModalRootActions = useCustomerModuleModalRootActionsStore();
  const isOpenCreateLabelCustomerModal = useMemo(() => {
    return customerModuleModalRootActions.checkIsOpenModal('CreateLabelCustomerModal');
  }, [customerModuleModalRootActions]);
  const showToast = useCustomToast();
  const form = useForm<TCreateLabelCustomerValidation>({
    resolver: zodResolver(createLabelCustomerValidationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      color: '',
    },
  });

  const mutationCreateLabelCustomer = useCreateLabelCustomerMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error adding label customer, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CUSTOMER_QUERY_KEY] })]);
        customerModuleModalRootActions.closeModal('CreateLabelCustomerModal');
        form.reset();
        showToast({
          message: 'Label customer created successfully',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitCreateLabelCustomer = useCallback(
    (data: TCreateLabelCustomerValidation) => {
      mutationCreateLabelCustomer.mutate({
        name: data?.name,
        color: data?.color,
      });
    },
    [mutationCreateLabelCustomer]
  );

  const handleClose = useCallback(() => {
    customerModuleModalRootActions.closeModal('CreateLabelCustomerModal');
    form.reset();
  }, [customerModuleModalRootActions, form]);
  return (
    <Modal isOpen={isOpenCreateLabelCustomerModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Add Label Customer</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitCreateLabelCustomer)(e);
          }}
          id="create-label-customer-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-customer" placeholder="Enter Label Customer Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-customer" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(GENERAL_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="create-label-customer-form"
          type="submit"
          disabled={mutationCreateLabelCustomer.isPending}
        >
          {mutationCreateLabelCustomer.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
CreateLabelCustomerModal.displayName = 'CreateLabelCustomerModal';
export default CreateLabelCustomerModal;
